import { createApi } from "@reduxjs/toolkit/query/react";

import { axiosBaseQuery } from "@/api/base";
import { OpenChatConversation, OpenChatMessage, OpenChatMessageType } from "@/types/open-chat";

const TAG = "openChat";
const TAG_CONVERSATION = "openChatConversation";

export const openChatApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/pf-open-chat/" }),
  reducerPath: "openChatApi",
  tagTypes: [TAG, TAG_CONVERSATION],
  endpoints: builder => ({
    getConversations: builder.query<OpenChatConversation[], void>({
      providesTags: result => (result || []).map(row => ({ type: TAG_CONVERSATION, id: row.id })),
      query: () => ({
        method: "GET",
        url: "conversations/",
      }),
    }),

    updateConversation: builder.mutation<OpenChatConversation, { id: string; data: { name: string } }>({
      invalidatesTags: result => [
        result ? { type: TAG_CONVERSATION, id: result.id } : { type: TAG_CONVERSATION, id: "LIST" },
      ],
      query: ({ id, data }) => ({
        method: "PATCH",
        url: `conversations/${id}`,
        data,
      }),
    }),

    getMessages: builder.query<OpenChatMessage[], string>({
      providesTags: (_result, _error, conversationId) => [{ type: TAG, id: conversationId }],
      query: conversationId => ({
        method: "GET",
        url: `query/${conversationId}`,
      }),
      transformResponse(response: OpenChatMessage[]) {
        return response.map(message => ({ ...message, completed: true }));
      },
    }),
    sendMessage: builder.mutation<
      OpenChatConversation | null,
      {
        conversationId: string;
        message: string;
        messageId: string;
        prompt: "open-chat-prompt-generator" | "open-chat-style-prompt";
      }
    >({
      query: ({ conversationId, message, messageId, prompt }) => ({
        url: `query/${conversationId}`,
        method: "POST",
        data: { message, message_id: messageId, prompt },
      }),
      async onQueryStarted({ conversationId, message, messageId }, { dispatch, queryFulfilled }) {
        dispatch(
          openChatApi.util.updateQueryData("getMessages", conversationId, (data = []) => [
            ...data,
            { id: messageId, type: "human" as OpenChatMessageType, content: message },
          ]),
        );
        const { data: conversation } = await queryFulfilled;
        if (conversation) {
          dispatch(
            openChatApi.util.updateQueryData("getConversations", undefined, (conversations: OpenChatConversation[]) => {
              if (!conversations.some(row => row.id === conversation.id)) {
                conversations.push(conversation);
              }
            }),
          );
        }
      },
    }),
  }),
});

export const { useSendMessageMutation, useGetMessagesQuery, useGetConversationsQuery, useUpdateConversationMutation } =
  openChatApi;
